import React, {CSSProperties, FC, PointerEvent, useEffect, useRef, useState} from "react";
import {DownloadOutlined} from "@ant-design/icons";
import {Button, Typography} from "antd";
import {buildAuthorizedFileFetchOptions, resolveFileDownloadId} from "../../fileDownloadUrl";
import {A6_TEMPLATE_CANVAS} from "./templateCanvas";
import {normalizeTemplateTextFieldStyle} from "./templateTextFields";
import type {TemplateTextFieldStyle} from "./templateTextFields";

export {A6_TEMPLATE_CANVAS};

export type TemplatePlacementKind = "text" | "qr";

export type TemplatePlacement = {
    key: string;
    label: string;
    sample: string;
    x: number;
    y: number;
    kind?: TemplatePlacementKind;
    size?: number;
    style?: TemplateTextFieldStyle;
};

type TemplatePoint = {
    x: number;
    y: number;
};

type TemplatePlacementEditorProps = {
    authToken?: string;
    backgroundUrl?: string | null;
    backgroundAlt: string;
    downloadText?: string;
    canDownloadPreview?: boolean;
    downloadingPreview?: boolean;
    emptyText: string;
    logicalWidth: number;
    logicalHeight: number;
    placements: TemplatePlacement[];
    onDownloadPreview?: () => void | Promise<void>;
    onPlacementChange: (key: string, point: TemplatePoint) => void;
};

const clamp = (value: number, min: number, max: number) =>
    Math.max(min, Math.min(max, value));

const isPdfContent = (url: string, contentType?: string) =>
    contentType?.toLowerCase().includes("pdf") || /\.pdf(?:$|[?#])/i.test(url);

const colorToRgba = (color: string, opacity: number) => {
    const match = /^#([0-9a-f]{6})$/i.exec(color);
    if (!match) {
        return color;
    }

    const value = match[1];
    const red = parseInt(value.slice(0, 2), 16);
    const green = parseInt(value.slice(2, 4), 16);
    const blue = parseInt(value.slice(4, 6), 16);

    return `rgba(${red}, ${green}, ${blue}, ${opacity})`;
};

const buildDecorationStyle = (style?: TemplateTextFieldStyle): CSSProperties => {
    const normalized = normalizeTemplateTextFieldStyle(style);

    return {
        backgroundColor: colorToRgba(normalized.backgroundColor, normalized.backgroundOpacity),
        border: `${normalized.borderWidth}px solid ${colorToRgba(normalized.borderColor, normalized.borderOpacity)}`,
        borderRadius: normalized.borderRadius,
        boxShadow: normalized.shadowEnabled ? "0 4px 14px rgba(15, 23, 42, 0.18)" : "none"
    };
};

const buildTextPlacementStyle = (style?: TemplateTextFieldStyle): CSSProperties => {
    const normalized = normalizeTemplateTextFieldStyle(style);

    return {
        ...buildDecorationStyle(normalized),
        color: normalized.color,
        fontFamily: normalized.fontFamily,
        fontSize: normalized.fontSize,
        fontStyle: normalized.fontStyle,
        fontWeight: normalized.fontWeight
    };
};

export const calculateTemplatePointFromClientPosition = (
    clientX: number,
    clientY: number,
    rect: Pick<DOMRect, "left" | "top" | "width" | "height">,
    logicalWidth: number,
    logicalHeight: number
): TemplatePoint => ({
    x: Math.round(clamp(((clientX - rect.left) / rect.width) * logicalWidth, 0, logicalWidth)),
    y: Math.round(clamp(((clientY - rect.top) / rect.height) * logicalHeight, 0, logicalHeight))
});

export const TemplatePlacementEditor: FC<TemplatePlacementEditorProps> = ({
    authToken,
    backgroundUrl,
    backgroundAlt,
    downloadText = "Download preview",
    canDownloadPreview,
    downloadingPreview = false,
    emptyText,
    logicalWidth,
    logicalHeight,
    placements,
    onDownloadPreview,
    onPlacementChange
}) => {
    const previewRef = useRef<HTMLDivElement | null>(null);
    const draggingKeyRef = useRef<string | null>(null);
    const trimmedBackgroundUrl = backgroundUrl?.trim() ?? "";
    const protectedBackgroundFileId = resolveFileDownloadId(trimmedBackgroundUrl);
    const [resolvedBackground, setResolvedBackground] = useState<{
        contentType?: string;
        url: string;
    }>({url: ""});

    useEffect(() => {
        if (!trimmedBackgroundUrl || !protectedBackgroundFileId) {
            setResolvedBackground({url: ""});
            return;
        }

        let isCancelled = false;
        let objectUrl = "";

        setResolvedBackground({url: ""});

        fetch(trimmedBackgroundUrl, buildAuthorizedFileFetchOptions(authToken))
            .then(response => {
                if (!response.ok) {
                    throw new Error("Template background download failed");
                }

                return response.blob();
            })
            .then(blob => {
                objectUrl = URL.createObjectURL(blob);
                if (isCancelled) {
                    URL.revokeObjectURL(objectUrl);
                    return;
                }

                setResolvedBackground({
                    contentType: blob.type,
                    url: objectUrl
                });
            })
            .catch(() => {
                if (!isCancelled) {
                    setResolvedBackground({url: ""});
                }
            });

        return () => {
            isCancelled = true;
            if (objectUrl) {
                URL.revokeObjectURL(objectUrl);
            }
        };
    }, [authToken, protectedBackgroundFileId, trimmedBackgroundUrl]);
    const previewBackground = protectedBackgroundFileId
        ? resolvedBackground
        : {url: trimmedBackgroundUrl};

    const updatePlacementFromPointer = (key: string, event: PointerEvent<HTMLElement>) => {
        const rect = previewRef.current?.getBoundingClientRect();
        if (!rect) {
            return;
        }

        onPlacementChange(key, calculateTemplatePointFromClientPosition(
            event.clientX,
            event.clientY,
            rect,
            logicalWidth,
            logicalHeight));
    };

    const handlePointerMove = (event: PointerEvent<HTMLDivElement>) => {
        if (!draggingKeyRef.current) {
            return;
        }

        event.preventDefault();
        updatePlacementFromPointer(draggingKeyRef.current, event);
    };

    const stopDragging = (event: PointerEvent<HTMLDivElement>) => {
        if (draggingKeyRef.current) {
            previewRef.current?.releasePointerCapture?.(event.pointerId);
        }

        draggingKeyRef.current = null;
    };

    return (
        <div style={{display: "grid", gap: 16}}>
            <div style={{maxWidth: "100%", overflowX: "auto", paddingBottom: 4}}>
                <div
                    ref={previewRef}
                    onPointerMove={handlePointerMove}
                    onPointerUp={stopDragging}
                    onPointerCancel={stopDragging}
                    style={{
                        aspectRatio: `${logicalWidth} / ${logicalHeight}`,
                        background: "var(--ati-soft)",
                        border: "1px solid var(--ati-line)",
                        borderRadius: 8,
                        height: `${logicalHeight}px`,
                        overflow: "hidden",
                        position: "relative",
                        touchAction: "none",
                        width: `${logicalWidth}px`
                    }}
                >
                    {previewBackground.url ? (
                        isPdfContent(previewBackground.url, previewBackground.contentType) ? (
                            <object
                                aria-label={backgroundAlt}
                                data={previewBackground.url}
                                type={previewBackground.contentType || "application/pdf"}
                                style={{
                                    height: "100%",
                                    inset: 0,
                                    pointerEvents: "none",
                                    position: "absolute",
                                    width: "100%"
                                }}
                            />
                        ) : (
                            <img
                                alt={backgroundAlt}
                                src={previewBackground.url}
                                style={{
                                    height: "100%",
                                    inset: 0,
                                    objectFit: "contain",
                                    pointerEvents: "none",
                                    position: "absolute",
                                    width: "100%"
                                }}
                            />
                        )
                    ) : (
                        <div style={{
                            alignItems: "center",
                            color: "var(--ati-muted)",
                            display: "flex",
                            height: "100%",
                            justifyContent: "center",
                            padding: 24,
                            textAlign: "center"
                        }}>
                            <Typography.Text type="secondary">{emptyText}</Typography.Text>
                        </div>
                    )}

                    {placements.map(placement => {
                        const left = `${(placement.x / logicalWidth) * 100}%`;
                        const top = `${(placement.y / logicalHeight) * 100}%`;
                        const isQr = placement.kind === "qr";
                        const placementStyle = isQr
                            ? buildDecorationStyle(placement.style)
                            : buildTextPlacementStyle(placement.style);
                        const qrSize = placement.size ?? 64;

                        return (
                            <div
                                key={placement.key}
                                role="button"
                                tabIndex={0}
                                aria-label={placement.label}
                                onPointerDown={event => {
                                    draggingKeyRef.current = placement.key;
                                    previewRef.current?.setPointerCapture?.(event.pointerId);
                                    updatePlacementFromPointer(placement.key, event);
                                }}
                                style={{
                                    alignItems: "center",
                                    color: "#111827",
                                    cursor: "grab",
                                    display: "inline-flex",
                                    fontWeight: 700,
                                    justifyContent: "center",
                                    left,
                                    height: isQr ? qrSize : undefined,
                                    minHeight: isQr ? qrSize : 28,
                                    minWidth: isQr ? qrSize : 96,
                                    overflow: isQr ? "hidden" : undefined,
                                    padding: isQr ? 4 : "4px 10px",
                                    position: "absolute",
                                    top,
                                    transform: "translate(-50%, -50%)",
                                    userSelect: "none",
                                    width: isQr ? qrSize : undefined,
                                    zIndex: 2,
                                    ...placementStyle
                                }}
                            >
                                {isQr ? (
                                    <span style={{
                                        alignItems: "center",
                                        alignSelf: "stretch",
                                        background: "#ffffff",
                                        color: "#111827",
                                        display: "flex",
                                        flex: 1,
                                        justifyContent: "center"
                                    }}>
                                        {placement.sample}
                                    </span>
                                ) : placement.sample}
                            </div>
                        );
                    })}
                </div>
            </div>

            {(canDownloadPreview ?? !!previewBackground.url) && onDownloadPreview && (
                <div style={{maxWidth: 840}}>
                    <Button
                        icon={<DownloadOutlined/>}
                        loading={downloadingPreview}
                        onClick={() => void onDownloadPreview()}
                    >
                        {downloadText}
                    </Button>
                </div>
            )}

        </div>
    );
};

export default TemplatePlacementEditor;
